import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ContentChild, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ISnListResponse, ISnListQueryParameters, ISnFeedQueryParameters } from '@lib/api';

import { SnInfiniteScrollComponent } from '../infinite-scroll/infinite-scroll.component';
import { SnPullToRefreshComponent } from '../pull-to-refresh/pull-to-refresh.component';
import { SnListDataSource } from './list.data-source';
import { ISnListItem } from './list.interfaces';
import { SnListReference } from './list.reference';
import { SnListOperation } from './list.utils';

@Component({
  selector: 'sn-data-list',
  templateUrl: './list.component.html',
  host: {
    class: 'sn-data-list',
  },
})
export class SnDataListComponent<M extends ISnListItem> implements OnDestroy {
  @Input()
  set fetchFn(fn: (params: ISnListQueryParameters) => Observable<ISnListResponse<M>>) {
    this.reference.fetchFn = fn;
  }

  @Input()
  set feedFn(fn: (params: ISnFeedQueryParameters) => Observable<M[]>) {
    this.reference.feedFn = fn;
  }

  @Input()
  set reloadByQueryParams(value: boolean) {
    this._reference.reloadByQueryParams = coerceBooleanProperty(value);
  }

  @Input()
  set enableCursorPagination(value: boolean) {
    this._reference.enableCursorPagination = coerceBooleanProperty(value);
  }

  @ContentChild(SnPullToRefreshComponent)
  public set pullToRefreshComponent(component: SnPullToRefreshComponent) {
    if (component) {
      component.refresh$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this.reference.reload();
      });

      this.dataSource.updated$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        component.refreshCompleted();
      });
    }
  }

  @ContentChild(SnInfiniteScrollComponent)
  public set infiniteScrollComponent(component: SnInfiniteScrollComponent) {
    if (this._infiniteScrollComponent) {
      return;
    }
    this._infiniteScrollComponent = component;
    if (component) {
      component.load$
        .pipe(
          debounceTime(100),
          filter(() => !this.reference.isComplete),
          takeUntil(this._destroy$),
        )
        .subscribe(() => {
          component.loadStarted();
          if (
            this.reference.operation !== SnListOperation.Reload &&
            this.reference.operation !== SnListOperation.Init
          ) {
            this.reference.operation = SnListOperation.Load;
            this.reference.nextPage();
          }
          this.reference.load();
        });

      this.dataSource.updated$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        component.loadCompleted();

        if (this.reference.isComplete) {
          component.complete();
        }
      });

      this.reference.reload$.pipe(takeUntil(this._destroy$)).subscribe(() => {
        component.reload();
      });
    }
  }

  private _infiniteScrollComponent: SnInfiniteScrollComponent;

  private readonly _reference: SnListReference<M>;
  private readonly _destroy$ = new Subject<void>();

  constructor(activatedRoute: ActivatedRoute) {
    this._reference = new SnListReference<M>(activatedRoute);
    this.reference.reload();
  }

  get reference(): SnListReference<M> {
    return this._reference;
  }

  get dataIsInited(): boolean {
    return this._reference.inited;
  }

  get dataSource(): SnListDataSource<M> {
    return this._reference.dataSource;
  }

  public ngOnDestroy(): void {
    this._reference.destroy();

    this._destroy$.next();
    this._destroy$.complete();
  }
}
